// Dog walking mini-game: collect bones, avoid squirrels, then bring the dog home
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sound } from "@/lib/sound";

const GRID = 6;
const NEEDED = 3;
const HOME = { x: GRID - 1, y: GRID - 1 };
const rand = (n) => Math.floor(Math.random() * n);
const randCell = (avoid) => {
  let c;
  do c = { x: rand(GRID), y: rand(GRID) };
  while (avoid.some((a) => a.x === c.x && a.y === c.y));
  return c;
};

const DogWalkGame = ({ onFinish }) => {
  const [g, setG] = useState(() => {
    const start = { x: 0, y: 0 };
    const bones = [];
    for (let i = 0; i < 4; i++) bones.push(randCell([start, HOME, ...bones]));
    const squirrels = [randCell([start, HOME, ...bones]), randCell([start, HOME, ...bones])];
    return { dog: start, bones, squirrels, score: 0, misses: 0, done: false };
  });
  const [elapsed, setElapsed] = useState(0);
  const [flash, setFlash] = useState(null);
  const gRef = useRef(g);
  gRef.current = g;
  const elapsedRef = useRef(0);
  elapsedRef.current = elapsed;

  useEffect(() => {
    const tick = setInterval(() => setElapsed((t) => t + 1), 1000);
    const mover = setInterval(() => {
      setG((s) => {
        if (s.done) return s;
        const squirrels = s.squirrels.map((q) => ({
          x: (q.x + rand(3) - 1 + GRID) % GRID,
          y: (q.y + rand(3) - 1 + GRID) % GRID,
        }));
        return { ...s, squirrels };
      });
    }, 900);
    return () => { clearInterval(tick); clearInterval(mover); };
  }, []);

  const finish = (s) => {
    const timeBonus = elapsedRef.current <= 20 ? 1 : 0;
    const rating = Math.max(1, Math.min(5, 3 + Math.min(1, s.score - NEEDED) + timeBonus - s.misses));
    Sound.success();
    setTimeout(() => onFinish({ score: s.score, misses: s.misses, rating }), 500);
  };

  const move = (dx, dy) => {
    const s = gRef.current;
    if (s.done) return;
    const nx = Math.max(0, Math.min(GRID - 1, s.dog.x + dx));
    const ny = Math.max(0, Math.min(GRID - 1, s.dog.y + dy));
    let { bones, score, misses } = s;
    const bi = bones.findIndex((b) => b.x === nx && b.y === ny);
    if (bi !== -1) {
      score += 1;
      bones = bones.filter((_, i) => i !== bi);
      bones.push(randCell([{ x: nx, y: ny }, HOME, ...bones]));
      Sound.coin();
      setFlash("+1 bone!");
    }
    if (s.squirrels.some((q) => q.x === nx && q.y === ny)) {
      misses += 1;
      Sound.error();
      setFlash("Squirrel! The dog pulled the leash!");
    }
    const atHome = nx === HOME.x && ny === HOME.y;
    let done = false;
    if (atHome) {
      if (score >= NEEDED) done = true;
      else setFlash(`Not yet! Find ${NEEDED - score} more bone${NEEDED - score === 1 ? "" : "s"} first.`);
    }
    const next = { ...s, dog: { x: nx, y: ny }, bones, score, misses, done };
    setG(next);
    if (done) finish(next);
    setTimeout(() => setFlash(null), 900);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowUp") { e.preventDefault(); move(0, -1); }
      if (e.key === "ArrowDown") { e.preventDefault(); move(0, 1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); move(-1, 0); }
      if (e.key === "ArrowRight") { e.preventDefault(); move(1, 0); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []); // eslint-disable-line

  const homeOpen = g.score >= NEEDED;

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex justify-between items-center mb-3 font-heading font-black">
        <div className="bg-sky-100 border-2 border-sky-300 rounded-xl px-3 py-1 text-sky-800" data-testid="dog-score">
          🦴 {g.score}/{NEEDED}
        </div>
        <div className="bg-rose-100 border-2 border-rose-300 rounded-xl px-3 py-1 text-rose-800" data-testid="dog-misses">
          🐿️ {g.misses}
        </div>
        <div className="bg-amber-100 border-2 border-amber-300 rounded-xl px-3 py-1 text-amber-800" data-testid="dog-timer">
          ⏱ {elapsed}s
        </div>
      </div>
      <p className="text-center text-sm font-semibold mb-3 h-5" data-testid="dog-hint">
        {flash ? (
          <span className="text-sky-800">{flash}</span>
        ) : homeOpen ? (
          <span className="text-emerald-700">Great! Now walk the dog 🏠 home to finish the job.</span>
        ) : (
          <span className="text-slate-600">Collect {NEEDED} 🦴 bones, avoid 🐿️ squirrels, then head 🏠 home.</span>
        )}
      </p>
      <div
        className="grid gap-1 bg-emerald-50 border-4 border-emerald-300 rounded-2xl p-2 aspect-square mx-auto"
        style={{ gridTemplateColumns: `repeat(${GRID}, 1fr)`, width: "min(100%, 40vh)" }}
      >
        {Array.from({ length: GRID * GRID }).map((_, i) => {
          const x = i % GRID;
          const y = Math.floor(i / GRID);
          const isDog = g.dog.x === x && g.dog.y === y;
          const isBone = g.bones.some((b) => b.x === x && b.y === y);
          const isSquirrel = g.squirrels.some((s) => s.x === x && s.y === y);
          const isHome = HOME.x === x && HOME.y === y;
          return (
            <div
              key={`cell-${x}-${y}`}
              data-testid={isHome ? "dog-home-cell" : undefined}
              className={`flex items-center justify-center rounded-md text-xl sm:text-2xl aspect-square ${
                isHome ? (homeOpen ? "bg-emerald-300 animate-pulse" : "bg-slate-200") : "bg-white/70"
              }`}
            >
              {isDog ? "🐕" : isBone ? "🦴" : isSquirrel ? "🐿️" : isHome ? (homeOpen ? "🏠" : "🔒") : ""}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-3 gap-1.5 mt-3 max-w-[200px] mx-auto">
        <div />
        <Button onClick={() => move(0, -1)} className="bg-sky-500 hover:bg-sky-600 rounded-xl btn-3d border-2 border-sky-600 text-xl py-3" data-testid="dog-up">↑</Button>
        <div />
        <Button onClick={() => move(-1, 0)} className="bg-sky-500 hover:bg-sky-600 rounded-xl btn-3d border-2 border-sky-600 text-xl py-3" data-testid="dog-left">←</Button>
        <Button onClick={() => move(0, 1)} className="bg-sky-500 hover:bg-sky-600 rounded-xl btn-3d border-2 border-sky-600 text-xl py-3" data-testid="dog-down">↓</Button>
        <Button onClick={() => move(1, 0)} className="bg-sky-500 hover:bg-sky-600 rounded-xl btn-3d border-2 border-sky-600 text-xl py-3" data-testid="dog-right">→</Button>
      </div>
    </div>
  );
};

export default DogWalkGame;
